import { Switch, Tooltip } from "antd";
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import type { AutoTopupConfigResponse } from "store/slices/reduxApi/billing";

// Shared validation bounds. Keep in sync with the server-side bounds in
// `auto-topup/config.ts` (server is the source of truth — these mirror it
// for client-side error messages so admins don't need a round trip).
export const PACK_MIN = 50;
export const PACK_MAX = 2500;
// No upper bound on monthly cap (the server matches — see
// auto-topup/constants.ts). The cap is an admin-chosen safety net,
// not a product-imposed ceiling.

// First-time defaults when the org has no saved auto-topup config yet. The
// form pre-fills with these so the admin sees a ready-to-save form instead of
// three empty inputs they have to figure out themselves. All surfaces share
// the same fallback (Manage Plan modal `TopUpView`, /spending
// `CreditRefillsCard`, /usage `AutoTopUpPanel`) so the first-run experience
// is consistent regardless of where the admin lands.
export const DEFAULT_PACK_CREDITS = 100;
export const DEFAULT_MONTHLY_CAP = 1000;
export const DEFAULT_TRIGGER_BUFFER = 20;

export type AutoFormState = {
  enabled: boolean;
  packCredits: string;
  monthlyCreditCap: string;
  triggerBufferCredits: string;
};

export type AutoErrors = {
  packCredits?: string;
  monthlyCreditCap?: string;
  triggerBufferCredits?: string;
};

export function formatUsd(cents: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

export function isWholeNumber(raw: string): boolean {
  if (raw.trim() === "") return false;
  const n = Number(raw);
  return Number.isFinite(n) && Number.isInteger(n);
}

export function autoFormFromServer(
  data: AutoTopupConfigResponse,
): AutoFormState {
  // Null-from-server means "first-run" (no saved config yet); fall back to the
  // shared first-run defaults so every surface — modal, /spending, /usage —
  // shows the same starter values. After the first save the server returns
  // concrete numbers and the fallback no longer applies.
  //
  // monthlyCreditCap is special-cased: the UI no longer renders an
  // input for it, and the server treats null as "no cap" (unlimited).
  // Leave it as empty string in the form state when the server returns
  // null so the user never accidentally saves a legacy value.
  return {
    enabled: data.enabled,
    packCredits: data.pack_credits?.toString() ?? String(DEFAULT_PACK_CREDITS),
    monthlyCreditCap: data.monthly_credit_cap?.toString() ?? "",
    triggerBufferCredits:
      data.trigger_buffer_credits?.toString() ?? String(DEFAULT_TRIGGER_BUFFER),
  };
}

export function autoFormEqual(a: AutoFormState, b: AutoFormState): boolean {
  return (
    a.enabled === b.enabled &&
    a.packCredits === b.packCredits &&
    a.monthlyCreditCap === b.monthlyCreditCap &&
    a.triggerBufferCredits === b.triggerBufferCredits
  );
}

export function validateAutoForm(form: AutoFormState): AutoErrors {
  const errors: AutoErrors = {};
  if (!form.enabled) return errors;

  if (!isWholeNumber(form.packCredits)) {
    errors.packCredits = "Whole number required";
  } else {
    const pack = Number(form.packCredits);
    if (pack < PACK_MIN || pack > PACK_MAX) {
      errors.packCredits = `Must be between ${PACK_MIN} and ${PACK_MAX}`;
    }
  }

  const pack = Number(form.packCredits);
  // monthlyCreditCap is OPTIONAL (server-side default: null = "no cap").
  // The UI no longer renders an input for it, but the field still
  // lives in form state so legacy saved configs continue to round-trip.
  // Only validate when it's actually set; null/empty means unlimited.
  if (form.monthlyCreditCap.trim() !== "") {
    if (!isWholeNumber(form.monthlyCreditCap)) {
      errors.monthlyCreditCap = "Whole number required";
    } else {
      const cap = Number(form.monthlyCreditCap);
      if (isWholeNumber(form.packCredits) && cap < pack) {
        errors.monthlyCreditCap = "Must be >= pack size";
      }
    }
  }

  if (form.triggerBufferCredits.trim() !== "") {
    if (!isWholeNumber(form.triggerBufferCredits)) {
      errors.triggerBufferCredits = "Whole number required";
    } else {
      const buf = Number(form.triggerBufferCredits);
      if (buf < 0) {
        errors.triggerBufferCredits = "Must be >= 0";
      } else if (isWholeNumber(form.packCredits) && buf > pack) {
        errors.triggerBufferCredits = "Must be <= pack size";
      }
    }
  }

  return errors;
}

type AutoTopUpFormFieldsProps = {
  form: AutoFormState;
  onFormChange: (form: AutoFormState) => void;
  errors: AutoErrors;
  /** Per-credit price in cents, from `autoTopupConfig.unit_amount_cents`. */
  unitAmountCents: number;
  /** When false the switch is disabled and no charge can be configured. */
  hasDefaultPaymentMethod: boolean;
  /** Disable all controls (e.g. while a charge is in flight in TopUpView). */
  disabled?: boolean;
  /**
   * Optional explanatory copy for the disabled toggle. Surfaced as an antd
   * Tooltip wrapping the switch + label, so admins see WHY the toggle is
   * locked instead of staring at a dead control. Caller derives this from
   * `autoTopupConfig.unavailable_reason` (plan_ineligible / trialing /
   * canceling / canceled / feature_disabled / no_payment_method) — most
   * common on POC orgs that haven't added a card yet.
   */
  disabledTooltip?: string | null;
  /**
   * Optional content rendered at the right edge of the toggle row. Used by
   * `TopUpView` to host the inline "Save automatic refill settings" button
   * when the form is dirty (decoupled from the bottom Refill primary, which
   * only charges). `/spending`'s `CreditRefillsCard` doesn't use this slot
   * — it surfaces a Save bar at the bottom of the card instead.
   */
  switchRowSlot?: ReactNode;
};

/**
 * Auto top up form fields — shared between the Manage Plan modal's
 * `TopUpView` and the flat `CreditRefillsCard` on /spending so the two
 * surfaces stay visually identical (single source of truth for field
 * labels, descriptions, validation copy, and inline `= $X.XX` previews).
 *
 * Layout per Jesse's 2026-04-28 spec (Cursor-style):
 *   - Toggle row: explicit "Automatically purchase more credits when running
 *     low" + inline (Recommended) hint + sub-explainer about payment method.
 *   - 3 stacked field rows: name on top-left, description below, input + price
 *     preview stacked on top-right. Variable-width preview text (e.g.
 *     `= $1,250.00`) sits beneath the input so it never reflows the
 *     description column.
 *
 * The component is purely presentational: form state + errors are owned
 * by the parent so the parent can decide when (and whether) to persist.
 * `TopUpView` saves auto-refill independently via the inline `switchRowSlot`
 * button (decoupled from the manual charge primary); `/spending` fires a
 * standalone PATCH from `CreditRefillsCard`'s bottom Save bar (only
 * surfaced when the form is dirty).
 */
export function AutoTopUpFormFields({
  form,
  onFormChange,
  errors,
  unitAmountCents,
  hasDefaultPaymentMethod,
  disabled = false,
  disabledTooltip,
  switchRowSlot,
}: AutoTopUpFormFieldsProps) {
  // Show the disabled-reason tooltip only when the toggle is actually
  // unusable (no payment method or some other availability gate). Hiding
  // an empty/null tooltip prevents the antd wrapper from intercepting
  // hover when there's nothing to say.
  const switchDisabled = !hasDefaultPaymentMethod || disabled;
  const showDisabledTooltip = switchDisabled && !!disabledTooltip;
  const packCharge = isWholeNumber(form.packCredits)
    ? Number(form.packCredits) * unitAmountCents
    : null;
  const capCharge = isWholeNumber(form.monthlyCreditCap)
    ? Number(form.monthlyCreditCap) * unitAmountCents
    : null;

  return (
    <div className="flex flex-col gap-3">
      {/* Toggle row: explicit "what this does" phrase plus a green Recommended
          pill alongside the title. Pill style mirrors other "suggested action"
          chips (success-toned background + emerald text) so admins read it as
          guidance, not as a status badge. The pill sits inline with the title
          so it visually anchors to the row, not the description. */}
      <Tooltip
        title={showDisabledTooltip ? disabledTooltip : null}
        placement="top"
      >
        {/* span wrapper: antd Tooltip needs a non-disabled child to receive
            mouse events; a disabled <Switch> doesn't fire them. The span
            captures hover even when its child is inert. */}
        <span className="flex items-start gap-3">
          {/* Title + description on the LEFT, switch flushed to the RIGHT.
              Jesse's spec moved the toggle from leading-edge to trailing
              edge so the row reads as a Settings-style row (label on the
              left, control on the right) — matches the row pattern in
              SpendLimitsSection so /spending's surfaces feel uniform. */}
          <div className="flex flex-1 flex-col gap-0.5">
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] font-medium leading-[1.35] text-[#1f2633]">
              <span>Automatically purchase more credits when running low</span>
              <span
                data-test="auto-topup-recommended-pill"
                className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-[1px] text-[11px] font-medium leading-[1.4] text-emerald-700"
              >
                Recommended
              </span>
            </span>
            <span className="text-[11px] leading-[1.4] text-[#6c7689]">
              Charges the default payment method on file. You can pause anytime.
            </span>
          </div>
          <Switch
            data-test="auto-topup-switch"
            checked={form.enabled}
            disabled={switchDisabled}
            onChange={(checked) => onFormChange({ ...form, enabled: checked })}
            className="mt-0.5 shrink-0"
          />
          {switchRowSlot ? (
            <div className="shrink-0">{switchRowSlot}</div>
          ) : null}
        </span>
      </Tooltip>

      {/* Field rows: 2-column grid per row — left column has name (top) and
          description (below); right column has input (top) and price preview
          (below). Variable-width preview text (e.g. `= $1,250.00` vs
          `= $125.00`) sits underneath the input so it never reflows the
          description column.

          The whole grid is mounted only when auto-refill is on — empty input
          rows for a disabled feature read as "broken form" rather than "off".
          Form state still holds the values so flipping the toggle back on
          restores prior input without a re-fetch. */}
      {form.enabled ? (
        <div data-test="auto-topup-fields" className="flex flex-col">
          <AutoField
            label="Refill threshold"
            description="The number of credits remaining at which an automatic refill will be triggered."
            value={form.triggerBufferCredits}
            onChange={(v) => onFormChange({ ...form, triggerBufferCredits: v })}
            error={errors.triggerBufferCredits}
            min={0}
            max={
              isWholeNumber(form.packCredits)
                ? Number(form.packCredits)
                : undefined
            }
            unitSuffix="credits"
            pricePreview={null}
            disabled={disabled}
          />
          <AutoField
            label="Refill amount"
            description="The number of credits you'll automatically purchase per refill when you hit your refill threshold."
            value={form.packCredits}
            onChange={(v) => onFormChange({ ...form, packCredits: v })}
            error={errors.packCredits}
            min={PACK_MIN}
            max={PACK_MAX}
            pricePreview={
              packCharge != null ? `= ${formatUsd(packCharge)}` : null
            }
            disabled={disabled}
          />
          {/* Maximum refill amount is OPTIONAL. When the form value is
              empty we collapse the input behind a "+ Set monthly cap"
              affordance (mirrors SpendLimitsSection's null-state
              pattern). Clicking the + reveals the input; clearing the
              input back to empty + losing focus collapses it again.
              Saving with the cap empty sends monthly_credit_cap=null;
              the server's trigger pipeline charges a full pack each
              fire with no cumulative cycle ceiling. */}
          <OptionalAutoField
            label="Maximum refill amount"
            description="Optional. Maximum credits to auto-refill per month. Leave empty for no cap."
            value={form.monthlyCreditCap}
            onChange={(v) => onFormChange({ ...form, monthlyCreditCap: v })}
            error={errors.monthlyCreditCap}
            min={isWholeNumber(form.packCredits) ? Number(form.packCredits) : 0}
            pricePreview={
              capCharge != null ? `= ${formatUsd(capCharge)}` : null
            }
            disabled={disabled}
          />
        </div>
      ) : null}
    </div>
  );
}

function AutoField({
  label,
  description,
  value,
  onChange,
  error,
  min,
  max,
  pricePreview,
  unitSuffix,
  disabled,
  hideValue = false,
}: {
  label: string;
  /** Cursor-style explainer rendered below the field name. */
  description: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  min?: number;
  max?: number;
  /** e.g. `= $125.00`. Sits directly under the input box. */
  pricePreview: string | null;
  /** e.g. `credits`. Used when there's no $ preview to keep the row
   *  visually balanced with the others. */
  unitSuffix?: string;
  disabled: boolean;
  /**
   * When true, the input is forced into the disabled state with its visible
   * text hidden (color: transparent) and the price-preview hint hidden.
   * Used by the auto-refill toggle off state — fields stay rendered so the
   * modal doesn't jump in height when the toggle flips, but the user
   * doesn't see meaningless numbers.
   */
  hideValue?: boolean;
}) {
  return (
    <div
      className="grid items-start gap-x-4 border-t border-[#f1f3f5] py-3 last:border-b last:border-[#f1f3f5]"
      style={{ gridTemplateColumns: "1fr auto" }}
    >
      {/* Left column row 1: field name (vertically centered against the
          input on its right via line-height matching the input's 32px height). */}
      <label
        className={`text-[12.5px] font-medium leading-[32px] ${hideValue ? "text-[#b0b7c1]" : "text-[#1f2633]"}`}
      >
        {label}
      </label>

      {/* Right column spans both rows (input on top, price preview / unit
          suffix underneath). */}
      <div
        className="flex flex-col items-end gap-1"
        style={{ gridColumn: "2", gridRow: "1 / span 2" }}
      >
        {/* Click-to-edit affordance: borderless until hover/focus so the
            value reads as plain text in the resting state, then reveals
            the input chrome the moment the admin engages. Steppers are
            suppressed (Webkit + Firefox + textfield) so the digit's
            right edge stays anchored across all three rows — without
            this, hover-stepper rendering shifts the column horizontally
            and the column becomes visually noisy. */}
        <input
          type="number"
          min={min}
          max={max}
          step={1}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || hideValue}
          className={`h-8 w-[110px] rounded border px-2 text-right text-[13px] tabular-nums outline-none transition disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
            error
              ? "border-[#b91c1c]"
              : "border-transparent hover:border-[#e8eaed] focus:border-[#9ca3af]"
          } ${
            hideValue
              ? "!bg-[#f9fafb] !text-transparent placeholder:text-transparent"
              : "disabled:opacity-60"
          }`}
        />
        {/* Right-padding (9px = input border 1px + input px-2 right 8px)
            on the preview / unit suffix shifts its right-edge inward so
            it lines up with the digit's right-edge inside the input,
            instead of with the input element's right border. Without
            this the preview text floats ~9px further right than the
            digit it describes -- a small but persistently noticeable
            misalignment. */}
        {error ? (
          <span className="whitespace-nowrap pr-[9px] text-[11px] text-[#b91c1c]">
            {error}
          </span>
        ) : pricePreview ? (
          <span
            className={`whitespace-nowrap pr-[9px] text-[11px] tabular-nums ${hideValue ? "text-transparent" : "text-[#6c7689]"}`}
          >
            {pricePreview}
          </span>
        ) : unitSuffix ? (
          <span
            className={`whitespace-nowrap pr-[9px] text-[11px] ${hideValue ? "text-transparent" : "text-[#6c7689]"}`}
          >
            {unitSuffix}
          </span>
        ) : null}
      </div>

      {/* Left column row 2: description, sits underneath the field name. */}
      <span
        className={`mt-0.5 text-[11px] leading-[1.5] ${hideValue ? "text-[#cdd1d8]" : "text-[#6c7689]"}`}
        style={{ gridColumn: "1" }}
      >
        {description}
      </span>
    </div>
  );
}

/**
 * AutoField variant for optional fields. When the form value is empty AND
 * the user hasn't explicitly opened the editor, render a compact "+" button
 * with a label. Click to reveal the underlying `AutoField` (input + price
 * preview), pre-focused. Clearing back to empty + blurring the input
 * collapses to the + state on the next render -- so the empty state can
 * persist across saves without the input lingering.
 *
 * Mirrors `SpendLimitsSection`'s `InlineLimitEditor` empty-state pattern so
 * the two surfaces feel uniform. Used today only for "Maximum refill amount"
 * (no monthly cap by default); leaving it generic in case other optional
 * fields land later.
 */
function OptionalAutoField({
  label,
  description,
  value,
  onChange,
  error,
  min,
  max,
  pricePreview,
  disabled = false,
}: {
  label: string;
  description: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  min?: number;
  max?: number;
  pricePreview?: string | null;
  disabled?: boolean;
}) {
  // `expanded` survives transient empty drafts while the user is actively
  // editing -- if they type then delete, the input stays open so they can
  // type again without the row collapsing under them. Blur with empty
  // value re-collapses it; non-empty values keep it expanded across renders
  // (driven off `value` itself, no extra state to sync).
  const [expanded, setExpanded] = useState(false);
  const expandedNow = expanded || value.trim() !== "";
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the input on the tick after the admin clicks "+". The
  // expand-state flips synchronously but the input only mounts on the next
  // commit; useEffect waits for it.
  useEffect(() => {
    if (expanded && value.trim() === "" && inputRef.current) {
      inputRef.current.focus();
    }
  }, [expanded, value]);

  // Reset expanded when the form value becomes empty externally (e.g. a
  // server-driven reset or "Discard changes" wipes the field).
  useEffect(() => {
    if (value.trim() === "") {
      setExpanded(
        (prev) => prev && document.activeElement === inputRef.current,
      );
    }
  }, [value]);

  if (!expandedNow) {
    return (
      <div
        className="grid items-start gap-x-4 py-3"
        style={{ gridTemplateColumns: "1fr auto" }}
      >
        <div style={{ gridColumn: "1" }}>
          <div className="text-[12.5px] font-medium leading-[20px] text-[#1f2633]">
            {label}
          </div>
          <span className="mt-0.5 text-[11px] leading-[1.5] text-[#6c7689]">
            {description}
          </span>
        </div>
        <div
          style={{ gridColumn: "2" }}
          className="flex items-center justify-end"
        >
          {/* Match SpendLimitsSection's empty-state pattern: a single
              icon-only "+" button (h-8 w-8). The row's label +
              description already tell admins what the button does;
              doubling that into pill copy would just add visual noise
              compared to the Default-per-user-limit row above. */}
          <button
            type="button"
            data-test="auto-topup-cap-add"
            onClick={() => setExpanded(true)}
            disabled={disabled}
            aria-label={`Set ${label}`}
            className={
              disabled
                ? "inline-flex h-8 w-8 items-center justify-center rounded-md border-0 bg-transparent text-[18px] leading-none text-[#9CA3AF] opacity-50"
                : "inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-[18px] leading-none text-[#6c7689] transition hover:bg-[#f1f3f5] hover:text-[#1f2633]"
            }
          >
            ＋
          </button>
        </div>
      </div>
    );
  }

  // Expanded layout — input flush-right at the column boundary (so
  // its digit aligns vertically with the digits in the AutoField rows
  // above), X clear button absolutely positioned past the column edge
  // into the card's right padding (no inline space = no recession):
  //
  //   ┌──────────┐
  //   │       30 │
  //   └──────────┘
  //       credits
  //   ┌──────────┐
  //   │      100 │
  //   └──────────┘
  //     = $125.00
  //   ┌──────────┐  ✕   <- X overhangs into card padding
  //   │     1000 │
  //   └──────────┘
  //   = $1,250.00
  //
  // Cross-row alignment: every digit (30 / 100 / 1000) shares the
  // same right-edge X coordinate (column_right − 9px from input
  // border + px-2). Every preview line (credits / = $125.00 /
  // = $1,250.00) sits at the same right edge (pr-[9px]). The X is
  // the only element that lives outside the column — it overhangs
  // into the card's right padding, which is sized (px-9 = 36px on
  // CreditRefillsCard) to accommodate the 32px X cluster (gap 4 + X
  // 28) with 4px of breathing room before the card's overflow-hidden
  // boundary clips.
  const hasValue = value.trim() !== "";
  return (
    <div
      className="grid items-start gap-x-4 py-3"
      style={{ gridTemplateColumns: "1fr auto", gridTemplateRows: "auto auto" }}
    >
      <label
        className="text-[12.5px] font-medium leading-[32px] text-[#1f2633]"
        style={{ gridColumn: "1", gridRow: "1" }}
      >
        {label}
      </label>
      <div
        className="flex flex-col items-end gap-1"
        style={{ gridColumn: "2", gridRow: "1 / span 2" }}
      >
        <div className="relative">
          <input
            ref={inputRef}
            type="number"
            min={min}
            max={max}
            step={1}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={() => {
              // Collapse only if blank — otherwise stay open so admins
              // can see the saved value next to the row label.
              if (value.trim() === "") setExpanded(false);
            }}
            disabled={disabled}
            className={`h-8 w-[110px] rounded border px-2 text-right text-[13px] tabular-nums outline-none transition disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
              error
                ? "border-[#b91c1c]"
                : "border-transparent hover:border-[#e8eaed] focus:border-[#9ca3af]"
            }`}
          />
          {/* X clear button — absolutely positioned to the right of the
              input so the input itself stays flush-right at the column
              boundary (which is what keeps cross-row digit alignment
              with the AutoField rows above). The X box overhangs 32px
              into the card's right padding: gap 4px + button width
              28px. CreditRefillsCard pads to px-9 (36px) so the X has
              4px of clearance before the overflow-hidden clip. */}
          {hasValue ? (
            <button
              type="button"
              data-test="auto-topup-cap-remove"
              onClick={() => {
                onChange("");
                setExpanded(false);
              }}
              title="Remove cap"
              aria-label={`Remove ${label}`}
              disabled={disabled}
              className="absolute left-[calc(100%+4px)] top-1/2 inline-flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded border border-transparent bg-transparent text-[#9CA3AF] transition hover:border-[#E5E7EB] hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg
                aria-hidden="true"
                className="h-3.5 w-3.5"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M4 4l8 8M12 4l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : null}
        </div>
        {/* pr-[9px] on the preview/error span — same offset as the
            AutoField rows above, so the right edge of "= $X.XX" lands
            under the digit's right edge inside the input. The X
            button is out of inline flow (absolute), so the input is
            flush-right at the column boundary just like every other
            input in the form. */}
        {error ? (
          <span className="whitespace-nowrap pr-[9px] text-[11px] text-[#b91c1c]">
            {error}
          </span>
        ) : pricePreview ? (
          <span className="whitespace-nowrap pr-[9px] text-[11px] tabular-nums text-[#6c7689]">
            {pricePreview}
          </span>
        ) : null}
      </div>
      <span
        className="mt-0.5 text-[11px] leading-[1.5] text-[#6c7689]"
        style={{ gridColumn: "1", gridRow: "2" }}
      >
        {description}
      </span>
    </div>
  );
}
